import { BuildUri } from "./BuildUri";

import {
    Identity,
    SimpleIdentity,
    EventProvisioning,
    AccessLevelProvisioning,
    QuickProvisioningIdentity
} from "../model/Identity";

import { PatchRequest } from "../requests/PatchRequest";
import { IdentityRequest } from "../requests/IdentityRequest";
import {
    EventIdentityProvisionRequest,
    EventIdentityProvisionMultipleRequest
} from "../requests/EventIdentityProvisionRequest";
import { IdentityAccessProvisionRequest } from "../requests/IdentityAccessProvisionRequest";

import { NetworkManager } from "../context";
import { ExternalAccessProvisioningRequest } from "../requests/ExternalAccessProvisioningRequest";
import { ShiftSummarySpot } from "../model/EventSchedule";
import { PendingIdentityRequest } from "src/components/public/CreatePendingIdentity";

class IdentityEndpoints {
    getPendingIdentityList(clientId?: number): Promise<SimpleIdentity[]> {
        return NetworkManager.fetch(
            BuildUri("/identity/pending", {}, { clientId })
        );
    }

    approvePendingIdentity(identityId: number): Promise<SimpleIdentity> {
        return NetworkManager.fetch(
            BuildUri("/identity/{identityId}/approve", { identityId }),
            {
                method: "POST"
            }
        );
    }

    createPendingIdentity(request: PendingIdentityRequest): Promise<any> {
        return NetworkManager.fetch(BuildUri("/public/identity"), {
            method: "POST",
            body: request
        });
    }

    getIdentityCount(
        clientId?: number,
        search?: string,
        includeArchived?: boolean,
        includePending?: boolean
    ): Promise<{ count: number }> {
        return NetworkManager.fetch(
            BuildUri(
                "/identity/count",
                {},
                { clientId, search, includeArchived, includePending }
            )
        );
    }

    getIdentityList(
        clientId?: number,
        firstName?: string,
        lastName?: string,
        limit?: number,
        startAt?: number,
        search?: string,
        includeArchived?: boolean,
        includePending?: boolean
    ): Promise<SimpleIdentity[]> {
        return NetworkManager.fetch(
            BuildUri(
                "/identity",
                {},
                {
                    clientId,
                    firstName,
                    lastName,
                    limit,
                    startAt,
                    search,
                    includeArchived,
                    includePending
                }
            )
        );
    }

    getIdentity(identityId: number): Promise<Identity> {
        return NetworkManager.fetch(
            BuildUri("/identity/{identityId}", { identityId })
        );
    }

    getShiftsForIdentity(identityId: number): Promise<ShiftSummarySpot[]> {
        return NetworkManager.fetch(
            BuildUri("/identity/{identityId}/shifts", { identityId })
        );
    }

    createIdentity(request: IdentityRequest): Promise<Identity> {
        return NetworkManager.fetch(BuildUri("/identity"), {
            method: "POST",
            body: request
        });
    }

    editIdentity(identityId: number, request: PatchRequest): Promise<Identity> {
        return NetworkManager.fetch(
            BuildUri("/identity/{identityId}", { identityId }),
            {
                method: "PATCH",
                body: request
            }
        );
    }

    deleteIdentity(identityId: number): Promise<void> {
        return NetworkManager.fetch(
            BuildUri("/identity/{identityId}", { identityId }),
            {
                method: "DELETE"
            }
        );
    }

    provisionForEvent(
        identityId: number,
        eventId: number,
        request: EventIdentityProvisionRequest,
        lite: boolean = false
    ): Promise<any> {
        return NetworkManager.fetch(
            BuildUri("/identity/{identityId}/event-provision/{eventId}", {
                identityId,
                eventId
            }, lite ? { lite: true } : {}),
            {
                method: "POST",
                body: request
            }
        );
    }

    provisionMultipleForEvent(
        eventId: number,
        request: EventIdentityProvisionMultipleRequest
    ): Promise<void> {
        return NetworkManager.fetch(
            BuildUri("/identity/event-provision/{eventId}/multiple", {
                eventId
            }),
            {
                method: "POST",
                body: request
            }
        );
    }

    deleteEventProvisioning(
        identityId: number,
        eventId: number
    ): Promise<Identity> {
        return NetworkManager.fetch(
            BuildUri("/identity/{identityId}/event-provision/{eventId}", {
                identityId,
                eventId
            }),
            { method: "DELETE" }
        );
    }

    provisionForClient(
        identityId: number,
        clientId: number,
        request: IdentityAccessProvisionRequest
    ): Promise<Identity> {
        return NetworkManager.fetch(
            BuildUri("/identity/{identityId}/access-provision/{clientId}", {
                identityId,
                clientId
            }),
            {
                method: "POST",
                body: request
            }
        );
    }

    deleteClientProvisioning(
        identityId: number,
        clientId: number
    ): Promise<Identity> {
        return NetworkManager.fetch(
            BuildUri("/identity/{identityId}/access-provision/{clientId}", {
                identityId,
                clientId
            }),
            { method: "DELETE" }
        );
    }

    getIdentitySummary() {
        // TODO
    }

    getBadge(
        identityId: number,
        eventId: number = undefined,
        clientId: number = undefined
    ) {
        return NetworkManager.fetch(
            BuildUri(
                "/identity/{identityId}/badge",
                { identityId },
                { eventId, clientId }
            )
        );
    }

    getPartialIdentityList(
        eventId: number,
        includeGlobal?: boolean
    ): Promise<Array<QuickProvisioningIdentity>> {
        return NetworkManager.fetch(
            BuildUri("/identity/partial", {}, { eventId, includeGlobal })
        );
    }

    provisionExternalAccess(
        identityId: number,
        request: ExternalAccessProvisioningRequest
    ): Promise<Identity> {
        return NetworkManager.fetch(
            BuildUri("/identity/{identityId}/external-access", { identityId }),
            {
                method: "POST",
                body: request
            }
        );
    }

    removeExternalAccessProvisioning(
        provisioningId: number
    ): Promise<Identity> {
        return NetworkManager.fetch(
            BuildUri("/identity/external-access/{provisioningId}", {
                provisioningId
            }),
            {
                method: "DELETE"
            }
        );
    }

    editIdentityVaccineField(
        identityId: number,
        request: PatchRequest
    ): Promise<Identity> {
        return NetworkManager.fetch(
            BuildUri("/identity/{identityId}/vaccine", { identityId }),
            {
                method: "PATCH",
                body: request
            }
        );
    }

    getPasskeyCredentials(identityId: number): Promise<any[]> {
        return NetworkManager.fetch(
            BuildUri("/authentication/passkey/credentials/{identityId}", {
                identityId
            })
        );
    }

    deletePasskeyCredential(credentialId: number): Promise<any> {
        return NetworkManager.fetch(
            BuildUri("/authentication/passkey/credentials/{credentialId}", {
                credentialId
            }),
            {
                method: "DELETE"
            }
        );
    }
}

export default new IdentityEndpoints();
